import { bundleAssetUrl } from './bundleUrl';
import { canPlayMusic, isSoundMuted, subscribeAudioSettings } from './soundSettings';
import { AUDIO_USER_GESTURE_EVENTS, subscribeUserGestureUnlock } from './userGestureUnlock';

export const MUSIC_TRACK_FILES: readonly string[] = [
  'alex-morgan-lofi-chill-vlog-beats-573883.mp3',
  'alex-morgan-lofi-cocktail-bar-568153.mp3',
  'alex-morgan-lofi-coffee-shop-568150.mp3',
  'alex-morgan-lofi-jazz-melody-restaurant-560048.mp3',
  'alex-morgan-lofi-midnight-club-568164.mp3',
  'alex-morgan-lofi-restaurant-568157.mp3',
  'alex-morgan-lofi-study-rainy-night-568166.mp3',
  'alex-morgan-lofi-sunny-cafe-568156.mp3',
];

export const MUSIC_TRACK_URLS: readonly string[] = MUSIC_TRACK_FILES.map((filename) =>
  bundleAssetUrl('audio/music', filename),
);

export const MUSIC_ASSET_STATUS = 'PIXABAY_PLAYLIST' as const;
export const MUSIC_LOOP_URL: string | null = MUSIC_TRACK_URLS[0] ?? null;

/** Background soundtrack: human-approved mix, trimmed ~15% before production. */
export const MUSIC_GAIN = 0.068;
export const MUSIC_PRELOAD = 'none' as const;
export const MUSIC_USER_GESTURE_EVENTS = AUDIO_USER_GESTURE_EVENTS;
export const MUSIC_ERROR_SKIP_LIMIT = 4;

export type ParkMusicElement = {
  src: string;
  preload: string;
  loop: boolean;
  volume: number;
  paused: boolean;
  currentTime: number;
  play(): Promise<void> | void;
  pause(): void;
  addEventListener(type: 'ended' | 'error', listener: () => void): void;
  removeEventListener(type: 'ended' | 'error', listener: () => void): void;
};

export interface MusicHandle {
  muted: boolean;
  playing: boolean;
  assetStatus: typeof MUSIC_ASSET_STATUS;
  setMuted(muted: boolean): void;
  stop(): void;
}

type MusicHost = {
  handles: number;
  outputMuted: boolean;
  unlocked: boolean;
  urls: string[];
  index: number;
  lastUrl: string | null;
  errorSkips: number;
  element: ParkMusicElement | null;
  onEnded: (() => void) | null;
  onError: (() => void) | null;
  createElement: () => ParkMusicElement;
};

let host: MusicHost | null = null;
let unsubscribeSettings: (() => void) | null = null;
let unbindUserGestures: (() => void) | null = null;

function defaultCreateElement(): ParkMusicElement {
  return new Audio();
}

function getHost(): MusicHost {
  if (!host) {
    host = {
      handles: 0,
      outputMuted: isSoundMuted(),
      unlocked: false,
      urls: [...MUSIC_TRACK_URLS],
      index: 0,
      lastUrl: null,
      errorSkips: 0,
      element: null,
      onEnded: null,
      onError: null,
      createElement: defaultCreateElement,
    };
  }
  return host;
}

function currentUrl(target: MusicHost): string | null {
  return target.urls[target.index] ?? target.urls[0] ?? null;
}

function shouldPlay(target: MusicHost): boolean {
  return !target.outputMuted && canPlayMusic() && target.urls.length > 0;
}

function isMusicAudible(target: MusicHost): boolean {
  return Boolean(target.element && shouldPlay(target) && !target.element.paused);
}

function pauseElement(target: MusicHost): void {
  const element = target.element;
  if (!element) return;
  try {
    element.volume = 0;
    element.pause();
  } catch {
    // Element may already be disposed.
  }
}

export function nextPlaylistIndex(
  currentIndex: number,
  urls: readonly string[],
  lastUrl: string | null,
): number {
  const n = urls.length;
  if (n <= 1) return 0;
  let next = (currentIndex + 1) % n;
  if (urls[next] === lastUrl) next = (next + 1) % n;
  return next;
}

function sameTrackSrc(element: ParkMusicElement, url: string): boolean {
  if (element.src === url) return true;
  try {
    return element.src.endsWith(url);
  } catch {
    return false;
  }
}

function applyTrack(target: MusicHost, url: string, restart: boolean): void {
  const element = target.element;
  if (!element) return;
  try {
    element.preload = MUSIC_PRELOAD;
    element.loop = false;
    if (!sameTrackSrc(element, url)) {
      element.src = url;
      element.currentTime = 0;
    } else if (restart) {
      element.currentTime = 0;
    }
    element.volume = MUSIC_GAIN;
    target.lastUrl = url;
  } catch {
    // Media may already be disposed.
  }
}

function playCurrent(target: MusicHost, restart = false): void {
  const element = target.element;
  const url = currentUrl(target);
  if (!element || !url || !shouldPlay(target)) return;
  applyTrack(target, url, restart);
  try {
    void Promise.resolve(element.play()).catch(() => undefined);
  } catch {
    // Browser autoplay/media failure stays silent until a later gesture.
  }
}

function advanceTrack(target: MusicHost, reason: 'ended' | 'error'): void {
  if (!shouldPlay(target)) {
    pauseElement(target);
    return;
  }
  if (reason === 'error') {
    target.errorSkips += 1;
    if (target.errorSkips >= MUSIC_ERROR_SKIP_LIMIT) {
      pauseElement(target);
      return;
    }
  } else {
    target.errorSkips = 0;
  }
  target.index = nextPlaylistIndex(target.index, target.urls, target.lastUrl);
  playCurrent(target, true);
}

function ensureElement(target: MusicHost): ParkMusicElement | null {
  if (target.element) return target.element;
  if (target.urls.length === 0) return null;
  try {
    const element = target.createElement();
    element.preload = MUSIC_PRELOAD;
    element.loop = false;
    element.volume = 0;
    const onEnded = () => advanceTrack(target, 'ended');
    const onError = () => advanceTrack(target, 'error');
    element.addEventListener('ended', onEnded);
    element.addEventListener('error', onError);
    target.element = element;
    target.onEnded = onEnded;
    target.onError = onError;
    const url = currentUrl(target);
    if (url) {
      element.src = url;
      target.lastUrl = url;
    }
    return element;
  } catch {
    target.element = null;
    target.onEnded = null;
    target.onError = null;
    return null;
  }
}

function syncHost(target: MusicHost): void {
  if (!shouldPlay(target)) {
    pauseElement(target);
    return;
  }
  if (!target.unlocked && !target.element) return;
  const element = ensureElement(target);
  if (!element) return;
  try {
    element.volume = MUSIC_GAIN;
    if (element.paused) playCurrent(target);
  } catch {
    // Browser policy or disposed element stays silent.
  }
}

function disposeHost(target: MusicHost): void {
  pauseElement(target);
  if (target.element && target.onEnded) {
    target.element.removeEventListener('ended', target.onEnded);
  }
  if (target.element && target.onError) {
    target.element.removeEventListener('error', target.onError);
  }
  if (target.element) {
    try {
      target.element.src = '';
    } catch {
      // ignore
    }
  }
  target.element = null;
  target.onEnded = null;
  target.onError = null;
  target.unlocked = false;
  target.errorSkips = 0;
  target.lastUrl = null;
  target.index = 0;
}

function bindSettings(): void {
  if (unsubscribeSettings) return;
  unsubscribeSettings = subscribeAudioSettings(() => {
    if (host) syncHost(host);
  });
}

function unbindUserGestureUnlock(): void {
  if (!unbindUserGestures) return;
  unbindUserGestures();
  unbindUserGestures = null;
}

function bindUserGestureUnlock(): void {
  if (unbindUserGestures) return;
  unbindUserGestures = subscribeUserGestureUnlock(() => nudgeMusicResume());
}

function resolveUrls(options?: { url?: string | null; urls?: readonly string[] | null }): string[] {
  if (options && 'urls' in options) return [...(options.urls ?? [])];
  if (options && 'url' in options) return options.url ? [options.url] : [];
  return [...MUSIC_TRACK_URLS];
}

export function createMusicChannel(options?: {
  url?: string | null;
  urls?: readonly string[] | null;
  createElement?: () => ParkMusicElement;
}): MusicHandle {
  const target = getHost();
  target.handles += 1;
  if (options && ('url' in options || 'urls' in options)) {
    target.urls = resolveUrls(options);
    target.index = 0;
    target.lastUrl = null;
    target.errorSkips = 0;
  }
  if (options?.createElement) target.createElement = options.createElement;
  bindSettings();
  bindUserGestureUnlock();
  target.outputMuted = isSoundMuted();
  syncHost(target);

  const handle: MusicHandle = {
    muted: target.outputMuted,
    get playing() {
      return host === target && isMusicAudible(target);
    },
    assetStatus: MUSIC_ASSET_STATUS,
    setMuted(muted: boolean) {
      target.outputMuted = muted;
      handle.muted = muted;
      syncHost(target);
    },
    stop() {
      if (target.handles > 0) target.handles -= 1;
      if (target.handles > 0) return;
      disposeHost(target);
      host = null;
      unbindUserGestureUnlock();
      unsubscribeSettings?.();
      unsubscribeSettings = null;
    },
  };
  return handle;
}

export function nudgeMusicResume(): void {
  if (!host) return;
  host.unlocked = true;
  if (host.outputMuted || !canPlayMusic()) return;
  syncHost(host);
}

export function resetMusicHostForTests(): void {
  if (host) {
    disposeHost(host);
    host = null;
  }
  unbindUserGestureUnlock();
  unsubscribeSettings?.();
  unsubscribeSettings = null;
}
